import {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Layout} from '@/components/Layout';
import {
    Activity, TrendingUp, TrendingDown, Car, Ticket, AlertTriangle,
    MessageSquare, Phone, Users, ChevronRight, RefreshCw, Bell, UserPlus,
    ArrowLeftRight, Ban, Calendar, ArrowRight, Shield, UserCog
} from 'lucide-react';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Button} from '@/components/ui/button';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell} from 'recharts';
import {PermissionGate} from '@/components/PermissionGate';
import {
    api,
    getApiErrorMessage,
    massAlertEndpoints,
    occurrenceEndpoints,
    resourceContactRuleGroupEndpoints,
    resourceEndpoints,
    tagEndpoints,
    ticketEndpoints,
    userEndpoints,
} from '@/lib/api';

const statusConfig = {
    pending: {color: 'bg-red-100 text-red-700', label: 'Pendente'},
    open: {color: 'bg-red-100 text-red-700', label: 'Aberto'},
    in_progress: {color: 'bg-amber-100 text-amber-700', label: 'Em Curso'},
    resolved: {color: 'bg-emerald-100 text-emerald-700', label: 'Resolvido'},
    closed: {color: 'bg-slate-100 text-slate-600', label: 'Fechado'},
};

const priorityConfig = {
    alta: {color: 'bg-red-100 text-red-700', label: 'Alta'},
    normal: {color: 'bg-blue-100 text-blue-700', label: 'Normal'},
    baixa: {color: 'bg-slate-100 text-slate-600', label: 'Baixa'},
    critica: {color: 'bg-red-100 text-red-700', label: 'Crítica'},
};

const emptyDashboardData = {
    vehicles: [],
    users: [],
    ruleGroups: [],
    tags: [],
    tickets: [],
    incidents: [],
    alerts: [],
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const unwrapAttributes = (source = {}) =>
    source?.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? {...source, ...source.attributes}
        : source;

const arrayFrom = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return value.split(',').map(item => item.trim()).filter(Boolean);
        }
    }
    if (value && typeof value === 'object') return Object.values(value);
    return [];
};

const numberFrom = (...values) => {
    const value = values.find(item => item !== undefined && item !== null);
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const timestampFrom = (...values) => {
    const value = values.find(Boolean);
    const time = value ? new Date(value).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
};

const displayDateTime = (value) => value ? String(value).replace('T', ' ').slice(0, 16) : '';

const relativeTime = (value) => {
    const time = timestampFrom(value);
    if (!time) return '';
    const diffMinutes = Math.max(0, Math.round((Date.now() - time) / 60000));
    if (diffMinutes < 1) return 'Agora';
    if (diffMinutes < 60) return `Há ${diffMinutes} min`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `Há ${diffHours}h`;
    const diffDays = Math.round(diffHours / 24);
    if (diffDays < 7) return `Há ${diffDays}d`;
    return displayDateTime(value);
};

const normalizeDriver = (source = null) => {
    if (!source) return null;
    const driver = unwrapAttributes(source);
    const firstName = driver.first_name ?? driver.firstName ?? '';
    const lastName = driver.last_name ?? driver.lastName ?? '';
    const name = driver.name ?? driver.full_name ?? driver.display_name ?? `${firstName} ${lastName}`.trim() ?? driver.email ?? '';
    return name ? {id: driver.id ?? driver.user_id ?? driver.company_user_id, name} : null;
};

const normalizeResourceStatus = (resource = {}, statusRecord = {}) => {
    const statusName = String(statusRecord?.name ?? statusRecord?.title ?? resource.status ?? '').toLowerCase();
    if (statusName.includes('manuten') || statusName.includes('maintenance')) return 'maintenance';
    if (statusName.includes('inativ') || statusName.includes('inactive')) return 'inactive';
    const value = String(resource.status ?? '').toLowerCase();
    const map = {
        0: 'active',
        1: 'active',
        2: 'maintenance',
        3: 'inactive',
        active: 'active',
        ativo: 'active',
        maintenance: 'maintenance',
        manutencao: 'maintenance',
        inactive: 'inactive',
        inativo: 'inactive',
    };
    return map[value] || 'active';
};

const normalizeContactRuleGroupIds = (source = {}) => {
    const direct = arrayFrom(source.resource_contact_rule_group_ids ?? source.contact_rule_group_ids ?? source.contactRuleGroupIds);
    const relationGroups = collectionPayload(source, 'resource_contact_rule_groups').length
        ? collectionPayload(source, 'resource_contact_rule_groups')
        : collectionPayload(source, 'contact_rule_groups');
    const relationIds = relationGroups.map(group => group?.id ?? group?.resource_contact_rule_group_id ?? group?.contact_rule_group_id);
    const legacyId = source.resource_contact_rule_group_id ?? source.contact_rule_group_id ?? source.contact_rules_group_id;
    const ids = direct.length ? direct : relationIds.length ? relationIds : legacyId ? [legacyId] : [];
    return [...new Set(ids.map(id => String(id)).filter(Boolean))];
};

const normalizeResources = (payload) => {
    const resources = collectionPayload(payload, 'resources');
    const items = resources.length ? resources : collectionPayload(payload, 'vehicles');
    return items.map((source = {}, index) => {
        const resource = unwrapAttributes(source);
        const statusRecord = unwrapAttributes(resource.resource_status ?? resource.status_record ?? {});
        const tag = resource.tag ?? resource.gest_tag ?? {};
        const driver = normalizeDriver(resource.driver ?? resource.user ?? resource.assigned_user ?? resource.company_manager ?? resource.manager);
        const contactRuleGroupIds = normalizeContactRuleGroupIds(resource);
        return {
            id: source.id ?? resource.id ?? resource.resource_id ?? index + 1,
            status: normalizeResourceStatus(resource, statusRecord),
            driver,
            tag: tag?.id || tag?.code || resource.tag_id || resource.gest_tag_id ? tag : null,
            contactRuleGroupIds,
            contactRuleGroupId: contactRuleGroupIds[0] ?? null,
        };
    }).filter(resource => resource.id);
};

const normalizeUsers = (payload) =>
    collectionPayload(payload, 'users').map((source = {}, index) => {
        const user = unwrapAttributes(source);
        const roles = arrayFrom(user.roles)
            .map(role => typeof role === 'string' ? role : role?.name ?? role?.slug ?? role?.key)
            .filter(Boolean);
        return {
            id: source.id ?? user.id ?? user.user_id ?? index + 1,
            status: String(user.status ?? '').toLowerCase(),
            roles,
        };
    }).filter(user => user.id);

const normalizeRuleGroups = (payload) => {
    const groups = collectionPayload(payload, 'resource_contact_rule_groups');
    const contactGroups = groups.length ? groups : collectionPayload(payload, 'contact_rule_groups');
    const items = contactGroups.length ? contactGroups : collectionPayload(payload, 'groups');
    return items.map((group, index) => ({
        id: group?.id ?? group?.resource_contact_rule_group_id ?? group?.contact_rule_group_id ?? index + 1,
    })).filter(group => group.id);
};

const normalizeTagStatus = (tag, resource) => {
    if (tag.deactivated_at || tag.gest_tag_deactivation_reason_id || tag.deactivate_reason_id) return 'inativa';
    if (tag.gest_resource_id || tag.resource_id || resource?.id) return 'atribuída';
    const value = String(tag.status ?? '').toLowerCase();
    const map = {
        0: 'armazém',
        1: 'atribuída',
        2: 'inativa',
        available: 'armazém',
        warehouse: 'armazém',
        armazem: 'armazém',
        assigned: 'atribuída',
        atribuida: 'atribuída',
        active: 'ativa',
        ativa: 'ativa',
        inactive: 'inativa',
        inativa: 'inativa',
    };
    return map[value] || 'armazém';
};

const normalizeTags = (payload) =>
    collectionPayload(payload, 'tags').map(source => {
        const tag = unwrapAttributes(source);
        const resource = unwrapAttributes(tag.gest_resource ?? tag.resource ?? {});
        const reference = tag.reference ?? tag.serial_no ?? tag.code ?? source.id ?? tag.id;
        return {
            id: String(reference || ''),
            status: normalizeTagStatus(tag, resource),
        };
    }).filter(tag => tag.id);

const normalizeTicketStatus = (status) => {
    const value = String(status ?? '').toLowerCase();
    const map = {
        0: 'open',
        1: 'in_progress',
        2: 'resolved',
        3: 'closed',
        open: 'open',
        aberto: 'open',
        pending: 'open',
        in_progress: 'in_progress',
        em_progresso: 'in_progress',
        progress: 'in_progress',
        resolved: 'resolved',
        resolvido: 'resolved',
        closed: 'closed',
        fechado: 'closed',
    };
    return map[value] || 'open';
};

const normalizePriority = (priority) => {
    const value = String(priority ?? '').toLowerCase();
    const map = {
        low: 'baixa',
        baixa: 'baixa',
        normal: 'normal',
        medium: 'normal',
        high: 'alta',
        alta: 'alta',
        critical: 'critica',
        critica: 'critica',
    };
    return map[value] || value || 'normal';
};

const normalizeTickets = (payload) =>
    collectionPayload(payload, 'tickets').map((source = {}, index) => {
        const ticket = unwrapAttributes(source);
        const occurrence = ticket.occurrence ?? ticket.incident ?? {};
        const assignee = ticket.assignee ?? ticket.assigned_user ?? ticket.user ?? ticket.technician ?? {};
        const id = source.id ?? ticket.id ?? index + 1;
        const createdAt = ticket.created_at ?? ticket.createdAt;
        return {
            id: ticket.code ?? ticket.reference ?? ticket.ticket_no ?? `TKT-${id}`,
            title: ticket.type ?? ticket.title ?? ticket.subject ?? ticket.occurrence_type_name ?? occurrence.type_name ?? occurrence.title ?? 'Ticket',
            priority: normalizePriority(ticket.priority),
            status: normalizeTicketStatus(ticket.status),
            assignedTo: assignee.name ?? assignee.full_name ?? ticket.assignee_name ?? ticket.assigned_user_name ?? 'Sem responsável',
            createdAt: relativeTime(createdAt),
            createdTime: timestampFrom(createdAt),
        };
    }).filter(ticket => ticket.id);

const normalizeOccurrenceStatus = (status) => {
    const value = String(status ?? '').toLowerCase();
    const map = {
        0: 'pending',
        1: 'in_progress',
        2: 'resolved',
        3: 'closed',
        pending: 'pending',
        pendente: 'pending',
        open: 'pending',
        in_progress: 'in_progress',
        progress: 'in_progress',
        em_progresso: 'in_progress',
        resolved: 'resolved',
        resolvida: 'resolved',
        closed: 'resolved',
        ignored: 'closed',
        ignorada: 'closed',
    };
    return map[value] || 'pending';
};

const normalizeOccurrences = (payload) => {
    const occurrences = collectionPayload(payload, 'occurrences');
    const items = occurrences.length ? occurrences : collectionPayload(payload, 'incidents');
    return items.map((source = {}, index) => {
        const occurrence = unwrapAttributes(source);
        const resource = occurrence.resource ?? occurrence.vehicle ?? occurrence.object ?? {};
        const tag = occurrence.tag ?? resource.tag ?? {};
        const type = occurrence.occurrence_type ?? occurrence.type ?? {};
        const occurredAt = occurrence.reported_at ?? occurrence.occurred_at ?? occurrence.created_at ?? occurrence.date;
        return {
            id: occurrence.code ?? occurrence.reference ?? occurrence.id ?? `INC-${index + 1}`,
            tag: tag.code ?? tag.tag_code ?? tag.id ?? occurrence.tag_code ?? occurrence.tag_id ?? '-',
            type: type.name ?? type.title ?? occurrence.type_name ?? occurrence.occurrence_type_name ?? occurrence.title ?? 'Ocorrência',
            location: resource.reference ?? resource.code ?? resource.name ?? occurrence.location ?? '-',
            time: relativeTime(occurredAt),
            occurredAt,
            createdTime: timestampFrom(occurredAt),
            status: normalizeOccurrenceStatus(occurrence.status),
        };
    }).filter(incident => incident.id);
};

const normalizeChannels = (channels) => {
    if (Array.isArray(channels)) return channels.filter(Boolean);
    if (typeof channels === 'string') {
        try {
            return normalizeChannels(JSON.parse(channels));
        } catch {
            return channels.split(',').map(channel => channel.trim()).filter(Boolean);
        }
    }
    if (channels && typeof channels === 'object') {
        return Object.entries(channels)
            .filter(([, enabled]) => enabled === true || enabled === 1 || enabled === '1')
            .map(([channel]) => channel);
    }
    return [];
};

const normalizeAlerts = (payload) =>
    collectionPayload(payload, 'alerts')
        .concat(collectionPayload(payload, 'mass_alerts'))
        .filter((alert, index, alerts) => alerts.indexOf(alert) === index)
        .map((source = {}, index) => {
            const alert = unwrapAttributes(source);
            const recipients = collectionPayload(alert, 'recipients').length
                ? collectionPayload(alert, 'recipients')
                : collectionPayload(alert, 'users');
            const sentAt = alert.sentAt ?? alert.sent_at ?? alert.scheduled_at ?? alert.created_at;
            return {
                id: alert.code ?? alert.reference ?? alert.id ?? `ALT-${index + 1}`,
                title: alert.title ?? alert.subject ?? 'Alerta',
                channels: normalizeChannels(alert.channels ?? alert.channel),
                recipients: numberFrom(alert.totalRecipients, alert.total_recipients, alert.recipients_count, recipients.length),
                delivered: numberFrom(alert.delivered, alert.delivered_count),
                time: relativeTime(sentAt),
                createdTime: timestampFrom(sentAt),
            };
        }).filter(alert => alert.id);

const buildTicketStatusData = (tickets) => [
    {name: 'Abertos', value: tickets.filter(ticket => ticket.status === 'open').length, color: '#ef4444'},
    {name: 'Em Curso', value: tickets.filter(ticket => ticket.status === 'in_progress').length, color: '#f59e0b'},
    {name: 'Resolvidos', value: tickets.filter(ticket => ticket.status === 'resolved').length, color: '#22c55e'},
    {name: 'Fechados', value: tickets.filter(ticket => ticket.status === 'closed').length, color: '#64748b'},
];

const buildIncidentTrendData = (incidents, period) => {
    const days = Math.max(1, Math.min(Number(period) || 7, 30));
    const formatter = new Intl.DateTimeFormat('pt-PT', {weekday: 'short'});
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return Array.from({length: days}, (_, index) => {
        const day = new Date(today);
        day.setDate(today.getDate() - (days - index - 1));
        const nextDay = new Date(day);
        nextDay.setDate(day.getDate() + 1);
        const dayIncidents = incidents.filter(incident => {
            const time = timestampFrom(incident.occurredAt);
            return time >= day.getTime() && time < nextDay.getTime();
        });
        return {
            name: days === 1 ? 'Hoje' : formatter.format(day).replace('.', ''),
            ocorrencias: dayIncidents.length,
            resolvidas: dayIncidents.filter(incident => incident.status === 'resolved').length,
        };
    });
};

const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 19) return 'Boa tarde';
    return 'Boa noite';
};

const formatDateLong = () => {
    const d = new Date();
    return d.toLocaleDateString('pt-PT', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
};

const KpiCard = ({label, value, sub, icon: Icon, accent, trend, onClick, testId}) => (
    <Card
        data-testid={testId}
        className="p-5 cursor-pointer hover:shadow-md transition-all border-l-4"
        style={{borderLeftColor: accent}}
        onClick={onClick}
    >
        <div className="flex items-center justify-between mb-3">
            <p className="text-slate-600 text-sm font-medium">{label}</p>
            <div className="p-1.5 rounded-lg" style={{backgroundColor: `${accent}15`}}>
                <Icon size={18} style={{color: accent}}/>
            </div>
        </div>
        <div className="flex items-end justify-between">
            <div>
                <p className="text-3xl font-secondary font-bold text-slate-900 leading-none">{value}</p>
                <p className="text-xs text-slate-500 mt-2">{sub}</p>
            </div>
            {trend !== undefined && trend !== null && (
                <div className={`flex items-center gap-1 text-xs font-semibold ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {trend >= 0 ? <TrendingUp size={12}/> : <TrendingDown size={12}/>}
                    {Math.abs(trend)}%
                </div>
            )}
        </div>
    </Card>
);

const HealthRow = ({label, done, total, color}) => {
    const pct = total ? Math.round((done / total) * 100) : 0;
    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-slate-700">{label}</span>
                <span className="text-sm font-bold text-slate-900">{done}<span className="text-slate-400 font-normal">/{total}</span></span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                    className="h-full rounded-full transition-all"
                    style={{width: `${pct}%`, backgroundColor: color}}
                />
            </div>
        </div>
    );
};

const RecentEmpty = ({loading, label}) => (
    <div className="p-4 text-center text-sm text-slate-500">
        {loading ? 'A carregar...' : `Sem ${label}.`}
    </div>
);

export const Dashboard = () => {
    const navigate = useNavigate();
    const [period, setPeriod] = useState('7');
    const [dashboardData, setDashboardData] = useState(emptyDashboardData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadDashboardData = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const [
                resourcesResponse,
                usersResponse,
                ruleGroupsResponse,
                tagsResponse,
                ticketsResponse,
                occurrencesResponse,
                alertsResponse,
            ] = await Promise.all([
                api.get(resourceEndpoints.list).catch(error => ({data: [], error})),
                api.get(userEndpoints.list).catch(error => ({data: [], error})),
                api.get(resourceContactRuleGroupEndpoints.list).catch(error => ({data: [], error})),
                api.get(tagEndpoints.list).catch(error => ({data: [], error})),
                api.get(ticketEndpoints.list).catch(error => ({data: [], error})),
                api.get(occurrenceEndpoints.list).catch(error => ({data: [], error})),
                api.get(massAlertEndpoints.list).catch(error => ({data: [], error})),
            ]);

            setDashboardData({
                vehicles: normalizeResources(resourcesResponse.data),
                users: normalizeUsers(usersResponse.data),
                ruleGroups: normalizeRuleGroups(ruleGroupsResponse.data),
                tags: normalizeTags(tagsResponse.data),
                tickets: normalizeTickets(ticketsResponse.data),
                incidents: normalizeOccurrences(occurrencesResponse.data),
                alerts: normalizeAlerts(alertsResponse.data),
            });

            const failedResponse = [
                resourcesResponse,
                usersResponse,
                ruleGroupsResponse,
                tagsResponse,
                ticketsResponse,
                occurrencesResponse,
                alertsResponse,
            ].find(response => response.error);

            if (failedResponse) {
                setError(getApiErrorMessage(failedResponse.error, 'Alguns dados do dashboard não foram carregados.'));
            }
        } catch (err) {
            setDashboardData(emptyDashboardData);
            setError(getApiErrorMessage(err, 'Não foi possível carregar o dashboard.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        loadDashboardData().finally(() => {
            if (!mounted) return;
        });
        return () => {
            mounted = false;
        };
    }, [loadDashboardData]);

    const stats = useMemo(() => {
        const {vehicles, users} = dashboardData;
        const total = vehicles.length;
        const active = vehicles.filter(v => v.status === 'active').length;
        const maintenance = vehicles.filter(v => v.status === 'maintenance').length;
        const withDriver = vehicles.filter(v => v.driver).length;
        const withoutDriver = total - withDriver;
        const withTag = vehicles.filter(v => v.tag).length;
        const withRule = vehicles.filter(v => v.contactRuleGroupId || v.contactRuleGroupIds?.length).length;
        const activeRules = vehicles.filter(v => (v.contactRuleGroupId || v.contactRuleGroupIds?.length) && !v.driver).length;

        const totalUsers = users.length;
        const activeUsers = users.filter(u => ['active', 'ativo', '1'].includes(u.status)).length;
        const condutores = users.filter(u => u.roles.some(role => String(role).toLowerCase().includes('condutor') || String(role).toLowerCase().includes('driver'))).length;
        const coverage = {
            driver: {done: withDriver, total, color: '#10b981'},
            rule: {done: withRule, total, color: '#3b82f6'},
            tag: {done: withTag, total, color: '#8b5cf6'},
        };
        const score = total ? Math.round(
            ((withDriver / total) * 0.4 +
                (withRule / total) * 0.3 +
                (withTag / total) * 0.3) * 100
        ) : 0;

        return {
            total, active, maintenance, withDriver, withoutDriver, withTag, withRule, activeRules,
            totalUsers, activeUsers, condutores, coverage, score,
        };
    }, [dashboardData]);

    const ticketStatusData = useMemo(() => buildTicketStatusData(dashboardData.tickets), [dashboardData.tickets]);
    const incidentTrendData = useMemo(() => buildIncidentTrendData(dashboardData.incidents, period), [dashboardData.incidents, period]);
    const recentTickets = useMemo(() => [...dashboardData.tickets].sort((a, b) => b.createdTime - a.createdTime).slice(0, 3), [dashboardData.tickets]);
    const recentIncidents = useMemo(() => [...dashboardData.incidents].sort((a, b) => b.createdTime - a.createdTime).slice(0, 3), [dashboardData.incidents]);
    const recentAlerts = useMemo(() => [...dashboardData.alerts].sort((a, b) => b.createdTime - a.createdTime).slice(0, 3), [dashboardData.alerts]);
    const availableTags = useMemo(() => dashboardData.tags.filter(tag => tag.status === 'armazem' || tag.status === 'ativa'), [dashboardData.tags]);
    const deactivatedTags = useMemo(() => dashboardData.tags.filter(tag => tag.status === 'inativa'), [dashboardData.tags]);

    const attentionItems = useMemo(() => {
        const items = [];
        if (stats.withoutDriver > 0) {
            items.push({
                id: 'no-driver',
                icon: UserCog,
                color: '#f59e0b',
                title: `${stats.withoutDriver} veículo${stats.withoutDriver > 1 ? 's' : ''} sem condutor`,
                desc: stats.activeRules > 0 ? `${stats.activeRules} regra(s) de contacto ativa(s)` : 'Sem regra de contacto associada',
                action: 'Ver frota',
                path: '/fleet',
            });
        }
        const noRuleCount = dashboardData.vehicles.filter(v => !(v.contactRuleGroupId || v.contactRuleGroupIds?.length)).length;
        if (noRuleCount > 0) {
            items.push({
                id: 'no-rule',
                icon: Bell,
                color: '#3b82f6',
                title: `${noRuleCount} veículo${noRuleCount > 1 ? 's' : ''} sem regra`,
                desc: 'Definir contactos para notificação',
                action: 'Gerir regras',
                path: '/contact-rules',
            });
        }
        if (deactivatedTags.length > 0) {
            items.push({
                id: 'damaged-tags',
                icon: Ban,
                color: '#ef4444',
                title: `${deactivatedTags.length} tag${deactivatedTags.length > 1 ? 's' : ''} em baixa`,
                desc: 'Perdidas ou danificadas - substituir',
                action: 'Ver tags',
                path: '/tags',
            });
        }
        const openTickets = ticketStatusData.find(t => t.name === 'Abertos')?.value || 0;
        if (openTickets > 0) {
            items.push({
                id: 'open-tickets',
                icon: Ticket,
                color: '#dc2626',
                title: `${openTickets} ticket${openTickets > 1 ? 's' : ''} por abrir`,
                desc: 'Requerem atribuição ou resposta',
                action: 'Ver tickets',
                path: '/tickets',
            });
        }
        return items;
    }, [dashboardData.vehicles, deactivatedTags.length, stats, ticketStatusData]);

    const quickActions = [
        {label: 'Novo Veículo', icon: Car, accent: '#3b82f6', path: '/fleet'},
        {label: 'Novo Utilizador', icon: UserPlus, accent: '#10b981', path: '/users'},
        {label: 'Nova Regra', icon: Bell, accent: '#8b5cf6', path: '/contact-rules'},
        {label: 'Trocar Condutor', icon: ArrowLeftRight, accent: '#f59e0b', path: '/fleet'},
    ];

    const openTickets = ticketStatusData.find(t => t.name === 'Abertos')?.value || 0;
    const inProgressTickets = ticketStatusData.find(t => t.name === 'Em Curso')?.value || 0;

    return (
        <Layout>
            <div className="space-y-6" data-testid="dashboard">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">
                                {getGreeting()}, Admin
                            </h1>
                        </div>
                        <p className="text-slate-500 font-primary text-sm flex items-center gap-2 capitalize">
                            <Calendar size={14}/>
                            {formatDateLong()}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Select value={period} onValueChange={setPeriod}>
                            <SelectTrigger className="w-40" data-testid="period-selector">
                                <SelectValue/>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">Hoje</SelectItem>
                                <SelectItem value="7">Últimos 7 dias</SelectItem>
                                <SelectItem value="30">Últimos 30 dias</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" data-testid="refresh-btn" disabled={loading} onClick={loadDashboardData}>
                            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`}/>
                            Atualizar
                        </Button>
                    </div>
                </div>

                {error && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                        {error}
                    </div>
                )}

                {attentionItems.length > 0 && (
                    <Card className="p-5 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="attention-panel">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center">
                                    <AlertTriangle className="text-white" size={18}/>
                                </div>
                                <div>
                                    <h2 className="font-secondary font-bold text-slate-900">Atenção Necessária</h2>
                                    <p className="text-xs text-slate-600">{attentionItems.length} item(ns) requerem a sua ação</p>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            {attentionItems.map(item => {
                                const Icon = item.icon;
                                return (
                                    <button
                                        key={item.id}
                                        data-testid={`attention-${item.id}`}
                                        onClick={() => navigate(item.path)}
                                        className="text-left p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all group"
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{backgroundColor: `${item.color}15`}}>
                                                <Icon size={14} style={{color: item.color}}/>
                                            </div>
                                            <span className="text-xs font-bold uppercase tracking-wide" style={{color: item.color}}>!</span>
                                        </div>
                                        <p className="font-secondary font-bold text-sm text-slate-900 leading-tight">{item.title}</p>
                                        <p className="text-xs text-slate-500 mt-1">{item.desc}</p>
                                        <p className="text-xs font-semibold mt-2 flex items-center gap-1 text-slate-700 group-hover:gap-2 transition-all">
                                            {item.action}
                                            <ArrowRight size={12}/>
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    </Card>
                )}

                <div>
                    <h3 className="text-xs font-secondary font-bold uppercase tracking-wider text-slate-500 mb-3">Ações Rápidas</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {quickActions.map(a => {
                            const Icon = a.icon;
                            return (
                                <button
                                    key={a.label}
                                    data-testid={`quick-${a.label}`}
                                    onClick={() => navigate(a.path)}
                                    className="group flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all"
                                >
                                    <div
                                        className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                                        style={{backgroundColor: `${a.accent}15`}}
                                    >
                                        <Icon size={20} style={{color: a.accent}}/>
                                    </div>
                                    <div className="flex-1 text-left">
                                        <p className="font-secondary font-bold text-sm text-slate-900">{a.label}</p>
                                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                            Abrir
                                            <ArrowRight size={10} className="transition-transform group-hover:translate-x-1"/>
                                        </p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <PermissionGate permission="gest_dashboard_read">
                        <KpiCard
                            testId="kpi-vehicles"
                            label="Frota"
                            value={`${stats.active}/${stats.total}`}
                            sub={`${stats.maintenance} em manutenção`}
                            icon={Car}
                            accent="#3b82f6"
                            onClick={() => navigate('/fleet')}
                        />
                        <KpiCard
                            testId="kpi-drivers"
                            label="Utilizadores"
                            value={stats.condutores || stats.totalUsers}
                            sub={`${stats.withoutDriver} veículo(s) sem condutor`}
                            icon={Users}
                            accent="#10b981"
                            onClick={() => navigate('/users')}
                        />
                        <KpiCard
                            testId="kpi-rules"
                            label="Regras Ativas"
                            value={stats.activeRules}
                            sub={`${dashboardData.ruleGroups.length} grupos criados`}
                            icon={Bell}
                            accent="#8b5cf6"
                            onClick={() => navigate('/contact-rules')}
                        />
                        <KpiCard
                            testId="kpi-tickets"
                            label="Tickets Abertos"
                            value={openTickets}
                            sub={`${inProgressTickets} em curso`}
                            icon={Ticket}
                            accent="#ef4444"
                            onClick={() => navigate('/tickets')}
                        />
                    </PermissionGate>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card className="lg:col-span-2 p-6" data-testid="fleet-health">
                        <div className="flex items-start justify-between mb-5">
                            <div>
                                <h2 className="font-secondary font-bold text-slate-900 flex items-center gap-2">
                                    <Shield size={18} className="text-blue-600"/>
                                    Saúde da Frota
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">Cobertura operacional em tempo real</p>
                            </div>
                            <div className="text-right">
                                <div className="flex items-baseline gap-1 justify-end">
                                    <span className="text-4xl font-secondary font-bold text-slate-900">{stats.score}</span>
                                    <span className="text-sm text-slate-500">/ 100</span>
                                </div>
                                <Badge
                                    className={
                                        stats.score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                                            stats.score >= 60 ? 'bg-amber-100 text-amber-700' :
                                                'bg-red-100 text-red-700'
                                    }
                                >
                                    {stats.score >= 80 ? 'Excelente' : stats.score >= 60 ? 'Atenção' : 'Crítico'}
                                </Badge>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <HealthRow label="Veículos com condutor" done={stats.coverage.driver.done} total={stats.coverage.driver.total} color={stats.coverage.driver.color}/>
                            <HealthRow label="Veículos com regra de contacto" done={stats.coverage.rule.done} total={stats.coverage.rule.total} color={stats.coverage.rule.color}/>
                            <HealthRow label="Veículos com tag ativa" done={stats.coverage.tag.done} total={stats.coverage.tag.total} color={stats.coverage.tag.color}/>
                        </div>
                        <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-3 gap-4 text-center">
                            <div>
                                <p className="text-2xl font-secondary font-bold text-emerald-600">{stats.withDriver}</p>
                                <p className="text-xs text-slate-500 mt-0.5">Com condutor</p>
                            </div>
                            <div>
                                <p className="text-2xl font-secondary font-bold text-blue-600">{stats.activeRules}</p>
                                <p className="text-xs text-slate-500 mt-0.5">Regras ativas</p>
                            </div>
                            <div>
                                <p className="text-2xl font-secondary font-bold text-purple-600">{availableTags.length}</p>
                                <p className="text-xs text-slate-500 mt-0.5">Tags disponíveis</p>
                            </div>
                        </div>
                    </Card>

                    <Card className="p-6" data-testid="tickets-pie">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-secondary font-bold text-slate-900">Estado dos Tickets</h2>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')}>
                                <ChevronRight size={14}/>
                            </Button>
                        </div>
                        <div className="h-[180px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={ticketStatusData} cx="50%" cy="50%" innerRadius={50} outerRadius={70} paddingAngle={2} dataKey="value">
                                        {ticketStatusData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color}/>
                                        ))}
                                    </Pie>
                                    <Tooltip/>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                            {ticketStatusData.map((item) => (
                                <div key={item.name} className="flex items-center gap-2 text-xs">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: item.color}}/>
                                    <span className="text-slate-600">{item.name}:</span>
                                    <span className="font-bold text-slate-900 ml-auto">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="font-secondary font-bold text-slate-900 flex items-center gap-2">
                                <Activity size={18} className="text-amber-600"/>
                                Tendência de Ocorrências
                            </h2>
                            <p className="text-xs text-slate-500 mt-0.5">Reportadas vs resolvidas no período selecionado</p>
                        </div>
                        <Badge variant="outline">{period === '1' ? 'Hoje' : `Últimos ${period} dias`}</Badge>
                    </div>
                    <div className="h-[260px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={incidentTrendData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                                <XAxis dataKey="name" stroke="#64748b" fontSize={12}/>
                                <YAxis stroke="#64748b" fontSize={12}/>
                                <Tooltip
                                    contentStyle={{backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px'}}
                                />
                                <Bar dataKey="ocorrencias" fill="#f59e0b" name="Reportadas" radius={[6, 6, 0, 0]}/>
                                <Bar dataKey="resolvidas" fill="#22c55e" name="Resolvidas" radius={[6, 6, 0, 0]}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card data-testid="recent-tickets">
                        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Ticket className="text-red-500" size={16}/>
                                <h2 className="font-secondary font-bold text-slate-900 text-sm">Tickets</h2>
                            </div>
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/tickets')}>
                                Todos <ChevronRight size={12}/>
                            </Button>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {recentTickets.map((ticket) => (
                                <div key={ticket.id} className="p-4 hover:bg-slate-50 cursor-pointer" onClick={() => navigate('/tickets')}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-secondary font-bold text-slate-900 text-sm">{ticket.id}</span>
                                        <Badge className={`${priorityConfig[ticket.priority]?.color || priorityConfig.normal.color} text-[10px]`}>
                                            {priorityConfig[ticket.priority]?.label || ticket.priority}
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-slate-700 leading-tight">{ticket.title}</p>
                                    <div className="flex items-center justify-between mt-2">
                                        <p className="text-xs text-slate-500 truncate mr-2">{ticket.createdAt}</p>
                                        <Badge className={`${statusConfig[ticket.status]?.color || statusConfig.open.color} text-[10px]`}>
                                            {statusConfig[ticket.status]?.label || ticket.status}
                                        </Badge>
                                    </div>
                                </div>
                            ))}
                            {recentTickets.length === 0 && <RecentEmpty loading={loading} label="tickets recentes"/>}
                        </div>
                    </Card>

                    <Card data-testid="recent-incidents">
                        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="text-amber-500" size={16}/>
                                <h2 className="font-secondary font-bold text-slate-900 text-sm">Ocorrências</h2>
                            </div>
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/incidents')}>
                                Todas <ChevronRight size={12}/>
                            </Button>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {recentIncidents.map((incident) => (
                                <div key={incident.id} className="p-4 hover:bg-slate-50 cursor-pointer" onClick={() => navigate('/incidents')}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-secondary font-bold text-slate-900 text-sm">{incident.id}</span>
                                        <span className="font-mono text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{incident.tag}</span>
                                    </div>
                                    <p className="text-sm text-slate-700 leading-tight">{incident.type}</p>
                                    <div className="flex items-center justify-between mt-2">
                                        <p className="text-xs text-slate-500 truncate mr-2">{incident.time}</p>
                                        <Badge className={`${statusConfig[incident.status]?.color || statusConfig.pending.color} text-[10px]`}>
                                            {statusConfig[incident.status]?.label || incident.status}
                                        </Badge>
                                    </div>
                                </div>
                            ))}
                            {recentIncidents.length === 0 && <RecentEmpty loading={loading} label="ocorrências recentes"/>}
                        </div>
                    </Card>

                    <Card data-testid="recent-alerts">
                        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Bell className="text-blue-500" size={16}/>
                                <h2 className="font-secondary font-bold text-slate-900 text-sm">Alertas</h2>
                            </div>
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/mass-alerts')}>
                                Todos <ChevronRight size={12}/>
                            </Button>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {recentAlerts.map((alert) => {
                                const fullyDelivered = alert.delivered === alert.recipients;
                                return (
                                    <div key={alert.id} className="p-4 hover:bg-slate-50 cursor-pointer" onClick={() => navigate('/mass-alerts')}>
                                        <p className="font-secondary font-bold text-slate-900 text-sm leading-tight">{alert.title}</p>
                                        <div className="flex items-center gap-2 mt-1.5">
                                            {alert.channels.map(c => (
                                                <span key={c} className="flex items-center gap-1 text-[10px] text-slate-500 uppercase font-medium">
                                                    {c === 'call' ? <Phone size={10}/> : <MessageSquare size={10}/>}
                                                    {c}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-between mt-2">
                                            <p className="text-xs text-slate-500">{alert.time}</p>
                                            <Badge className={`text-[10px] ${fullyDelivered ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {alert.delivered}/{alert.recipients}
                                            </Badge>
                                        </div>
                                    </div>
                                );
                            })}
                            {recentAlerts.length === 0 && <RecentEmpty loading={loading} label="alertas recentes"/>}
                        </div>
                    </Card>
                </div>
            </div>
        </Layout>
    );
};
